/* ============================================================================
   camados-app.js — die App im Gerät blättert beim Scrollen.

   Lars' Idee (22.09.2026): Statt eines Fotos ein Telefon, auf dem die App
   nachgebaut ist; beim Scrollen über die Seite läuft erst die Liste der Boxen
   durch, dann öffnet sich die Gruppe — so, als tippte jemand darauf.

   ── ZWEI BETRIEBSARTEN ─────────────────────────────────────────────────────
   * FREI (Grundzustand, schmal, reduzierte Bewegung): Das Gerät scrollt mit der
     Seite. Die Liste darin läuft etwas schneller als die Seite (das ist die
     Parallaxe), und wenn das Gerät etwa in der Mitte steht, wechselt es zur
     Gruppe.
   * ANGEHEFTET (breit): Die Bühne bleibt stehen, während man 900 px weiter
     scrollt, und in dieser Strecke passiert beides. Das ist die Apple-Art.

   ⚠️ ANHEFTEN NUR, WENN ES SICHER GEHT. `position: sticky` wirkt nicht, sobald
   irgendein Vorfahre `overflow: hidden/auto/scroll` trägt — Horizon setzt das
   an etlichen Stellen. Dann wäre der Abschnitt 900 px höher, die Bühne liefe
   trotzdem weg, und darunter stünde ein leeres Loch. Deshalb prüft
   darfAnheften() die ganze Ahnenkette und schaltet sonst in FREI. Ein Effekt,
   der misslingt, darf nie schlechter aussehen als gar keiner.

   ── WAS NIE PASSIERT ───────────────────────────────────────────────────────
   Das Skript versteckt nichts. Ohne es steht die Übersicht im Gerät, und alles
   ist lesbar. Mit `prefers-reduced-motion` gibt es kein Anheften und kein
   Laufen der Liste; die Ansicht wechselt nur, ohne Bewegung.
   ========================================================================== */

(function () {
  'use strict';

  var SANFT = window.matchMedia('(prefers-reduced-motion: reduce)');
  var BREIT = window.matchMedia('(min-width: 990px)');

  // Wann was passiert, als Anteil des Scrollwegs (0 … 1). Nach dem Öffnen blättert auch die Gruppe:
  // Mit Bass-Platz und „Technisch" unter der Lautstärke ist sie länger als der Bildschirm (Lars, 23.09.).
  var PHASEN = {
    angeheftet: { listeVon: 0.05, listeBis: 0.4, druck: 0.44, gruppe: 0.5, gruppeVon: 0.6, gruppeBis: 0.9 },
    // Frei läuft das Gerät mit der Seite: Ab p ≈ 0,6 rutscht es oben aus dem Bild — bis dahin muss die Gruppe
    // durchgeblättert sein (bei 0,8 wäre das Telefon schon zur Hälfte weg gewesen).
    frei: { listeVon: 0.2, listeBis: 0.43, druck: 0.45, gruppe: 0.49, gruppeVon: 0.51, gruppeBis: 0.63 },
  };

  function klemmen(x) { return x < 0 ? 0 : x > 1 ? 1 : x; }

  function kopfHoehe() {
    return parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
  }

  function darfAnheften(wurzel) {
    if (!BREIT.matches || SANFT.matches) return false;
    if (!window.CSS || !CSS.supports('position', 'sticky')) return false;
    for (var el = wurzel.parentElement; el && el !== document.body; el = el.parentElement) {
      var s = getComputedStyle(el);
      if (/hidden|auto|scroll/.test(s.overflowX + ' ' + s.overflowY)) return false;
    }
    return true;
  }

  function einrichten(wurzel) {
    if (wurzel.hasAttribute('data-ca-bereit')) return;
    wurzel.setAttribute('data-ca-bereit', '');

    var geraet = wurzel.querySelector('[data-ca-geraet]');
    var liste = wurzel.querySelector('[data-ca-liste]');
    var gruppe = wurzel.querySelector('[data-ca-gruppe]');
    if (!geraet || !liste) return;

    var angeheftet = false;
    var weg = 0;         // wie weit die Liste laufen kann (App-Pixel)
    var wegGruppe = 0;   // … und die Gruppe
    var zustand = '';
    var geplant = false;
    var sichtbar = false;
    var letzterFortschritt = -1;

    function messen() {
      angeheftet = darfAnheften(wurzel);
      wurzel.classList.toggle('ca--gepinnt', angeheftet);

      // Das Maß kommt im Grundzustand aus dem Stilblatt. Angeheftet muss das
      // Gerät zusätzlich unter den Kopfbereich passen, sonst ragt es heraus.
      wurzel.style.removeProperty('--ca-mass');
      if (angeheftet) {
        var frei = window.innerHeight - kopfHoehe() - 72;
        var mass = Math.min(0.8, Math.max(0.5, (frei - 20) / 780));
        wurzel.style.setProperty('--ca-mass', mass.toFixed(3));
      }
      weg = Math.max(0, liste.scrollHeight - liste.clientHeight);
      wegGruppe = gruppe ? Math.max(0, gruppe.scrollHeight - gruppe.clientHeight) : 0;
    }

    function fortschritt() {
      var h = window.innerHeight;
      if (angeheftet) {
        var oben = kopfHoehe();
        var r = wurzel.getBoundingClientRect();
        var strecke = r.height - (h - oben);
        return strecke > 0 ? klemmen((oben - r.top) / strecke) : 0;
      }
      var g = geraet.getBoundingClientRect();
      return klemmen((h - g.top) / (h + g.height));
    }

    function zeichnen() {
      geplant = false;
      var p = fortschritt();
      var ph = angeheftet ? PHASEN.angeheftet : PHASEN.frei;
      var ruhig = SANFT.matches;

      // Fortschritt als CSS-Variable --ca-p (0 … 1): Auf der Startseite blendet damit erst der Fänger aus
      // und dann der Text ein, während die App läuft (27.09.2026). Bei reduzierter Bewegung setzt das
      // Skript nichts; dann gilt der Rückfallwert des Abschnitts (wie ohne Skript).
      var pv = ruhig ? -2 : Math.round(p * 1000) / 1000;
      if (pv !== letzterFortschritt) {
        letzterFortschritt = pv;
        if (ruhig) wurzel.style.removeProperty('--ca-p');
        else wurzel.style.setProperty('--ca-p', String(pv));
      }

      var t = ruhig ? 0 : klemmen((p - ph.listeVon) / (ph.listeBis - ph.listeVon));
      liste.scrollTop = Math.round(weg * t);
      if (gruppe) {
        var tg = ruhig ? 0 : klemmen((p - ph.gruppeVon) / (ph.gruppeBis - ph.gruppeVon));
        gruppe.scrollTop = Math.round(wegGruppe * tg);
      }

      var neu = p >= ph.gruppe ? 'gruppe' : (!ruhig && p >= ph.druck ? 'druck' : 'boxen');
      if (neu !== zustand) {
        zustand = neu;
        wurzel.classList.toggle('ca--gruppe', neu === 'gruppe');
        wurzel.classList.toggle('ca--druck', neu === 'druck');
      }
    }

    function planen() {
      if (geplant || !sichtbar) return;
      geplant = true;
      window.requestAnimationFrame(zeichnen);
    }

    function neuMessen() {
      messen();
      geplant = false;
      zeichnen();
    }

    // Nur rechnen, solange der Abschnitt in der Nähe ist.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (eintraege) {
        sichtbar = eintraege[0].isIntersecting;
        if (sichtbar) planen();
      }, { rootMargin: '200px 0px' }).observe(wurzel);
    } else {
      sichtbar = true;
    }

    window.addEventListener('scroll', planen, { passive: true });
    window.addEventListener('resize', neuMessen);
    [SANFT, BREIT].forEach(function (mq) {
      if (mq.addEventListener) mq.addEventListener('change', neuMessen);
    });

    messen();
    zeichnen();
  }

  function start() {
    document.querySelectorAll('[data-ca]').forEach(einrichten);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
  // Horizon wechselt Seiten per View Transitions — dabei läuft kein neues
  // DOMContentLoaded (dieselbe Falle wie in camados.js).
  document.addEventListener('shopify:section:load', start);
  window.addEventListener('pageshow', function (e) { if (e.persisted) start(); });
})();
